import { FC, MouseEventHandler } from 'react';

import { Bin } from '@zooplus/icons';
import { TextBadge } from '@zooplus/ui-components';

import CardIcon from '../CardIcon/CardIcon';
import CreditCardInfo from '../CreditCardInfo/CreditCardInfo';
import StateBox from '../StateBox/StateBox';

import { useTranslation } from '@hooks/useTranslations';
import { CreditCardStatus } from '@types-internal';
import { CreditCard } from '@types-internal/ModernizePaymentTypes';
import { getVariant, getDataTestId, getCardIconType } from '@utils/cardUtils';
import { handleKeyDown } from '@utils/utils';

import styles from './CreditCardNew.module.scss';

export type CreditCardNewProps = {
  creditCard: CreditCard & { selected?: boolean } & { paymentMethod?: string | null };
  onDelete?: MouseEventHandler;
  onSelect?: MouseEventHandler;
  status?: CreditCardStatus;
};

const CreditCardNew: FC<CreditCardNewProps> = ({ creditCard, onDelete, onSelect, status }) => {
  const { t } = useTranslation();

  const isSessionCard = creditCard.pspIdentifier === '0';
  const isBancontactCard = creditCard.paymentMethod === 'BANCONTACT';
  const variant = getVariant(creditCard);
  const hasError = variant === 'error';

  const onDeleteHandler: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(e);
  };

  const dataTestId = getDataTestId(creditCard, isSessionCard, isBancontactCard);
  const cardIconType = getCardIconType(isBancontactCard, creditCard);

  return (
    <StateBox
      variant={variant}
      onClick={hasError ? undefined : onSelect}
      className="creditCard"
      hasHoverEffect={!!onDelete}
      data-testid={dataTestId}
      data-zta={dataTestId}>
      <div className={styles.creditCardRowContainer}>
        {cardIconType && (
          <CardIcon className={styles.cardIconNew} data-testid="ccIcon" type={cardIconType} />
        )}
        {variant === 'error' && creditCard.expired && (
          <TextBadge label={t('checkout.payment.creditcard.expired.message')} variant="negative" />
        )}
        {onDelete && (
          <span
            className={styles.delete}
            data-testid={isBancontactCard ? 'removeBCCard' : 'removeCC'}
            data-zta="removeCC"
            onClick={onDeleteHandler}
            onKeyDown={(e) => handleKeyDown(e, onDelete)}
            role="button"
            tabIndex={0}>
            <Bin />
          </span>
        )}
      </div>
      <div className={styles.cardHolderName}>{creditCard.holderName}</div>
      <CreditCardInfo
        isBancontactCard={isBancontactCard}
        lastFourDigits={creditCard.maskedNumber}
        expirationMonth={creditCard.expirationMonth}
        expirationYear={creditCard.expirationYear}
        expired={creditCard.expired}
      />
      {variant === 'selected' && status === 'loading' && (
        <div className={styles.loaderBackdrop}>
          <div className={styles.loaderLarge} />
        </div>
      )}
    </StateBox>
  );
};

export default CreditCardNew;
